import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "./AuthStateProvider";
import { AivaLogoIcon } from "../shared/components/icons";
import type { Channel } from "../shared/host";
import styles from "./SignInPage.module.css";

const HOST_LABELS: Record<Channel, string> = {
  outlook: "Outlook",
  word: "Word",
  powerpoint: "PowerPoint",
};

/** "2 hours", "30 minutes", "1 hour and 30 minutes": the idle window in words. */
function describeIdleWindow(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  return parts.join(" and ");
}

interface SignInPageProps {
  readonly channel?: Channel | null;
}

export function SignInPage({ channel = null }: SignInPageProps) {
  const { signIn, signedOutForInactivity, idleTimeoutMinutes } = useAuthState();
  const appLabel = (channel && HOST_LABELS[channel]) || "Office";

  // AIVA-7143: `signIn` runs several silent token round-trips (acquireTokenSilent
  // → ssoSilent) before the Office auth dialog is even requested — on classic
  // Outlook those fail slowly and fall through, leaving a multi-second gap in
  // which the button gave no feedback and read as unresponsive. Reflect the
  // in-flight sign-in immediately so the click "responds" the instant it lands.
  const [isSigningIn, setIsSigningIn] = useState(false);
  // §9.1 announcements: a sign-in failure must be surfaced with role="alert" so a
  // screen reader announces it. signIn logs internally but returned nothing to
  // show, so the page sat silent after a failed attempt.
  const [signInError, setSignInError] = useState<string | null>(null);
  // Guard the post-await state reset: a successful silent sign-in unmounts this
  // page (signedIn flips), so the finally must not setState on an unmounted node.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSignIn = useCallback(async () => {
    setIsSigningIn(true);
    setSignInError(null);
    try {
      // signIn settles when the dialog closes, so the button stays busy for the whole attempt.
      await signIn();
    } catch {
      // signIn logs its own failures internally — but the page must also SHOW the
      // failure (role="alert" below), so surface a retryable message here rather
      // than swallowing it silently. The finally re-enables the button.
      if (mountedRef.current) setSignInError("Sign-in didn't complete. Please try again.");
    } finally {
      if (mountedRef.current) setIsSigningIn(false);
    }
  }, [signIn]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logoWrap}>
          <AivaLogoIcon />
        </div>
        <h1 className={styles.heading}>Welcome to AiVA</h1>
        <p className={styles.tagline}>Your intelligent {appLabel} assistant</p>
        {/* Why the user is back here. The inactivity timeout can end a session
            while the pane sits untouched in a background window; landing on a
            bare sign-in screen with no explanation reads as the add-in having
            dropped them. `<output>` carries the status role implicitly, so it is
            announced without stealing focus from the sign-in button. The window
            stated here is the one AuthStateProvider's timer ran on (the Studio
            Session Persistence value, or its default). */}
        {signedOutForInactivity && (
          <output className={styles.inactivityNotice}>
            You were signed out after {describeIdleWindow(idleTimeoutMinutes)} of inactivity. Sign
            in to pick up where you left off.
          </output>
        )}
        <button
          type="button"
          className={styles.signInBtn}
          onClick={handleSignIn}
          disabled={isSigningIn}
          aria-busy={isSigningIn}
        >
          {isSigningIn ? "Signing in…" : "Sign in with SSO"}
        </button>
        {signInError && (
          <p className={styles.signInError} role="alert">
            {signInError}
          </p>
        )}
      </div>
      <p className={styles.footer}>Powered by Williams Lea</p>
    </div>
  );
}
